import { useCabinet } from './useCabinet';
import { getSessionTheme } from '../../config/themes';
import ThemeStripes from './ThemeStripes';

interface Props {
  activeSection: string;
  onSectionChange: (section: string) => void;
  userName: string;
  onLogout: () => void;
}

export default function CabinetSidebar({ activeSection, onSectionChange, userName, onLogout }: Props) {
  const { config } = useCabinet();
  const initials = userName.split(' ').slice(0, 2).map((w) => w[0]).join('');
  const industrial = getSessionTheme() === 'industrial';

  return (
    <aside className="w-72 flex-shrink-0 bg-background-50 border-r border-surface-3 flex flex-col min-h-screen">
      {/* Brand */}
      {industrial ? (
        <div className="px-6 py-5 border-b border-surface-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary-500">
            <i className="ri-shield-user-line text-on-primary text-lg" />
          </div>
          <span className="font-heading text-[13px] font-extrabold uppercase tracking-[0.06em] leading-tight text-ink">
            Портал<br />сотрудников
          </span>
        </div>
      ) : (
        <div className="px-6 py-5 border-b border-surface-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-primary-500/10 border border-primary-500/20">
            <i className="ri-shield-user-line text-primary-300 text-lg" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] text-foreground-500 tracking-widest uppercase">Корпоративный</span>
            <span className="text-sm font-semibold text-ink">Портал сотрудников</span>
          </div>
        </div>
      )}

      {/* Profile */}
      <div className="px-6 py-6 border-b border-surface-3">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className={industrial
              ? 'w-14 h-14 rounded-xl bg-primary-500 flex items-center justify-center text-on-primary font-heading font-extrabold text-lg select-none'
              : 'w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-on-primary font-bold text-lg select-none'}>
              {initials}
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-primary-500 rounded-full border-2 border-background-50" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink truncate">{userName}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{config.roleLabel}</p>
          </div>
        </div>

        {config.showOnboardingNotice && (
          <div className="mt-4 flex items-center gap-2 bg-accent-300/10 border border-accent-300/20 rounded-xl px-3 py-2.5">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-accent-200 text-sm" />
            </span>
            <span className="text-xs text-accent-200 leading-tight">
              Процесс трудоустройства ещё не завершен, часть функционала недоступна
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {config.sections.map((item) => {
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isActive
                  ? industrial
                    ? 'bg-primary-500 text-on-primary font-semibold'
                    : 'bg-primary-500/15 text-primary-300 border border-primary-500/20'
                  : 'text-foreground-600 hover:text-foreground-800 hover:bg-surface-1'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`} />
              </span>
              {item.title}
              {item.lockIcon && (
                <span className="ml-auto w-4 h-4 flex items-center justify-center">
                  <i className={`${item.lockIcon} text-xs ${industrial && isActive ? 'text-on-primary/60' : 'text-foreground-400'}`} />
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-4 py-4 border-t border-surface-3">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-500 hover:text-foreground-700 hover:bg-surface-1 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-r-line" />
          </span>
          Выйти из аккаунта
        </button>
      </div>

      <div className="px-6 py-4 border-t border-surface-3">
        {industrial && <ThemeStripes className="mb-3" />}
        <div className="relative">
          <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />
        </div>
        <p className="text-[10px] text-foreground-400 tracking-widest uppercase">© 2026 · Корпоративный портал</p>
      </div>
    </aside>
  );
}
